import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  FiMessageCircle,
  FiUser,
  FiZap,
  FiMenu,
  FiX,
  FiHome,
  FiSettings,
  FiLogOut,
  FiHeart,
  FiCalendar,
  FiHelpCircle,
  FiCheckCircle,
  FiChevronDown,
} from "react-icons/fi";
import { FaRocket } from "react-icons/fa";
import { FaFire } from "react-icons/fa";
import { GiRose } from "react-icons/gi";
import { MdExplore } from "react-icons/md";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../hooks/useAppStore";
import { selectMyProfile } from "../../store/slices/profileSlice";
import { logoutUser } from "../../store/slices/authSlice";
import { selectHasProAccess } from "../../store/slices/subscriptionSlice";
import {
  selectRoseBalance,
  fetchRoseBalance,
} from "../../store/slices/rosesSlice";
import { selectUnseenLikesCount } from "../../store/slices/likesSlice";
import { selectConversations } from "../../store/slices/messagingSlice";
import logo from "../../assets/images/logo.png";

export function AppHeader() {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const profile = useAppSelector(selectMyProfile);
  const isPro = useAppSelector(selectHasProAccess);
  const displayLabel =
    profile?.displayName || profile?.name || user?.email || "Me";
  const profileTo = "/profile/me";

  const roseBalance = useAppSelector(selectRoseBalance);
  const unseenLikes = useAppSelector(selectUnseenLikesCount);
  const conversations = useAppSelector(selectConversations);
  const unreadMessages = conversations.reduce(
    (sum, c) => sum + (c.unreadCount ?? 0),
    0,
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const profileIncompleteCount = profile?.isSetupComplete
    ? [
        !profile.bio,
        !profile.interests?.length,
        !profile.images?.length,
        !profile.relationshipGoals,
      ].filter(Boolean).length
    : 0;
  const showProfileDot = profileIncompleteCount > 0;

  useEffect(() => {
    dispatch(fetchRoseBalance());
  }, [dispatch]);

  const mobileNavItems = [
    { to: "/home", label: "Home", Icon: FiHome },
    { to: "/kings-and-queens", label: "Kings & Queens", Icon: FaFire },
    { to: "/likes", label: "Likes", Icon: FiHeart },
    { to: "/messages", label: "Messages", Icon: FiMessageCircle },
    { to: "/boosts", label: "Boosts", Icon: FaRocket },
    { to: "/events", label: "Events", Icon: FiCalendar },
    { to: profileTo, label: "My Profile", Icon: FiUser },
    { to: "/verify", label: "Verify Account", Icon: FiCheckCircle },
    { to: "/support", label: "Support", Icon: FiHelpCircle },
    { to: "/settings", label: "Settings", Icon: FiSettings },
  ];

  const handleLogout = async () => {
    setMenuOpen(false);
    setProfileMenuOpen(false);
    await dispatch(logoutUser());
    navigate("/login");
  };

  const topNavItems: { to: string; label: string; Icon: React.ElementType; badge: number }[] = [
    { to: "/home", label: "Discover", Icon: MdExplore, badge: 0 },
    { to: "/kings-and-queens", label: "K&Q", Icon: FaFire, badge: 0 },
    { to: "/likes", label: "Likes", Icon: FiHeart, badge: unseenLikes },
    { to: "/messages", label: "Messages", Icon: FiMessageCircle, badge: unreadMessages },
    { to: "/boosts", label: "Boosts", Icon: FaRocket, badge: 0 },
    { to: "/events", label: "Events", Icon: FiCalendar, badge: 0 },
  ];

  const profileMenuItems = [
    { to: profileTo, label: "My Profile", Icon: FiUser },
    { to: "/verify", label: "Verify Account", Icon: FiCheckCircle },
    { to: "/support", label: "Support", Icon: FiHelpCircle },
    { to: "/settings", label: "Settings", Icon: FiSettings },
    { to: "/subscription", label: "Subscription", Icon: FiZap },
  ];

  return (
    <>
      {/* ── Desktop top nav (lg+) ─────────────────────────────────────── */}
      <header className="hidden lg:flex fixed top-0 left-0 right-0 h-16 z-50 bg-[#0f0e0e] border-b border-white/10 items-center px-6 gap-4">
        {/* Logo */}
        <Link to="/home" className="flex-shrink-0 mr-2">
          <img src={logo} alt="Crays" className="h-10 w-auto" />
        </Link>

        {/* Primary nav */}
        <nav className="flex items-center gap-0.5 flex-1">
          {topNavItems.map(({ to, label, Icon, badge }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap
                ${
                  isActive
                    ? "bg-white/10 text-yellow"
                    : "text-offwhite/50 hover:text-offwhite hover:bg-white/5"
                }`
              }
            >
              <div className="relative flex-shrink-0">
                <Icon className="w-[17px] h-[17px]" />
                {badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-lg bg-magenta text-white text-[9px] font-bold flex items-center justify-center">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </div>
              {label}
            </NavLink>
          ))}

          {/* Roses */}
          <NavLink
            to="/roses"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap
              ${
                isActive
                  ? "bg-white/10 text-yellow"
                  : "text-offwhite/50 hover:text-offwhite hover:bg-white/5"
              }`
            }
          >
            <GiRose className="w-[17px] h-[17px] flex-shrink-0" />
            Roses
            <span className="text-xs font-bold text-pink-400 bg-pink-500/15 border border-pink-500/20 px-1.5 py-0.5 rounded-lg">
              {roseBalance}
            </span>
          </NavLink>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isPro && (
            <Link
              to="/subscription"
              className="flex items-center gap-1.5 bg-yellow/10 border border-yellow/20 hover:bg-yellow/15 text-yellow text-xs font-bold px-3 py-2 rounded-xl transition-colors"
            >
              <FiZap className="w-3.5 h-3.5" /> Upgrade PRO
            </Link>
          )}

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors group"
            >
              <div className="relative flex-shrink-0">
                <div className="w-7 h-7 rounded-full overflow-hidden bg-magenta/30 border-2 border-white/10 group-hover:border-magenta/50 transition-colors flex items-center justify-center">
                  {profile?.profilePhoto ? (
                    <img
                      src={profile.profilePhoto}
                      alt="Me"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FiUser className="w-3.5 h-3.5 text-offwhite/60" />
                  )}
                </div>
                {showProfileDot && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow border-2 border-[#0f0e0e]" />
                )}
              </div>
              <span className="text-offwhite/80 text-sm font-semibold max-w-[120px] truncate">
                {displayLabel}
              </span>
              <FiChevronDown
                className={`w-3.5 h-3.5 text-offwhite/30 flex-shrink-0 transition-transform duration-200 ${profileMenuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {profileMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileMenuOpen(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-52 z-50 bg-[#1a1818] border border-white/10 rounded-2xl shadow-2xl overflow-hidden py-1">
                  {showProfileDot && (
                    <Link
                      to="/profile/edit"
                      onClick={() => setProfileMenuOpen(false)}
                      className="mx-2 my-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-yellow/10 border border-yellow/25 hover:bg-yellow/20 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-yellow text-xs font-semibold">Complete your profile</p>
                        <p className="text-yellow/60 text-xs mt-0.5">
                          {profileIncompleteCount} item{profileIncompleteCount !== 1 ? "s" : ""} missing
                        </p>
                      </div>
                    </Link>
                  )}
                  {profileMenuItems.map(({ to, label, Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setProfileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-offwhite/70 hover:text-offwhite hover:bg-white/5 transition-colors"
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {label}
                    </Link>
                  ))}
                  <div className="border-t border-white/10 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-offwhite/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <FiLogOut className="w-4 h-4 shrink-0" /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile top bar (< lg) ─────────────────────────────────────── */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-charcoal/95 backdrop-blur-md border-b border-white/10 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <Link to="/home" className="flex-shrink-0">
            <img src={logo} alt="Crays" className="h-12 w-auto" />
          </Link>

          <div className="flex items-center gap-2">
            {!isPro && (
              <Link
                to="/subscription"
                className="flex items-center gap-1.5 bg-yellow/10 border border-yellow/30 hover:bg-yellow/20 text-yellow text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
              >
                <FiZap className="w-3.5 h-3.5" /> PRO
              </Link>
            )}
            <button
              onClick={() => setMenuOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-xl text-offwhite/70 hover:text-offwhite hover:bg-white/5 transition-colors"
              aria-label="Open menu"
            >
              <FiMenu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile drawer (< lg) ──────────────────────────────────────── */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          <div
            className="absolute top-0 right-0 h-full w-72 bg-charcoal border-l border-white/10 flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-magenta/30 border-2 border-white/10 flex items-center justify-center flex-shrink-0">
                  {profile?.profilePhoto ? (
                    <img
                      src={profile.profilePhoto}
                      alt="Me"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FiUser className="w-5 h-5 text-offwhite/60" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-offwhite font-semibold text-sm truncate">
                    {displayLabel}
                  </p>
                  <p className="text-offwhite/40 text-xs truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-offwhite/40 hover:text-offwhite hover:bg-white/5 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {showProfileDot && (
              <Link
                to="/profile/edit"
                onClick={() => setMenuOpen(false)}
                className="mx-4 mt-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-yellow/10 border border-yellow/25 hover:bg-yellow/20 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-yellow text-xs font-semibold">
                    Complete your profile
                  </p>
                  <p className="text-yellow/60 text-xs mt-0.5">
                    {profileIncompleteCount} item
                    {profileIncompleteCount !== 1 ? "s" : ""} still missing
                  </p>
                </div>
                <span className="text-yellow text-xs font-bold shrink-0">
                  →
                </span>
              </Link>
            )}

            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {mobileNavItems.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors
                    ${isActive ? "bg-white/10 text-yellow" : "text-offwhite/60 hover:text-offwhite hover:bg-white/5"}`
                  }
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </nav>

            <div className="px-3 py-4 border-t border-white/10 space-y-2">
              {!isPro && (
                <Link
                  to="/subscription"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-yellow/10 border border-yellow/25 text-yellow text-sm font-bold hover:bg-yellow/20 transition-colors"
                >
                  <FiZap className="w-5 h-5 shrink-0" /> Upgrade PRO
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-offwhite/50 hover:text-red-400 hover:bg-red-500/10 text-sm font-semibold transition-colors"
              >
                <FiLogOut className="w-5 h-5 shrink-0" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
